// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    HasProductContext,
    ProductContext,
    ProductContextModel,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadVariantChildren from "@insite/client-framework/Store/Data/Products/Handlers/LoadVariantChildren";
import { getVariantChildrenDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductListViewType } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAttributes, { ProductAttributesStyles } from "@insite/content-library/Components/ProductAttributes";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductDetailsVariantDropdown from "@keller/Overrides/Widgets/ProductDetails/ProductDetailsVariantDropdown";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import ProductPreviousPurchase from "@keller/App/Components/Product/ProductPreviousPurchase";
import ReorderPadProductAttributes from "@keller/App/Widgets/ReorderPad/ReorderPadProductAttributes";
import ProductDescription, { ProductDescriptionStyles } from "@keller/Overrides/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@keller/Overrides/Components/ProductPartNumbers";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// Reads the `isStocked` product property (populated from the ERP stock flag) tolerantly across
// possible casings/values so a variant child can be treated as in stock.
const isVariantChildStocked = (child: ProductModel): boolean => {
    const properties = child.properties as { [key: string]: string } | undefined;
    const raw = properties?.isStocked ?? properties?.IsStocked;
    if (raw === undefined || raw === null) {
        return false;
    }
    const value = String(raw).trim().toLowerCase();
    return value === "true" || value === "1" || value === "yes";
};

interface OwnProps {
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showAvailability: boolean;
    showAttributes: boolean;
    showImageBadges?: boolean;
    maxImageBadges?: number;
    showTextBadges?: boolean;
    maxTextBadges?: number;
    showPreviousPurchase: boolean;
    // XNG-Changes: add this option for ReorderPad page
    showReorderPadProductAttributes?: boolean;
    view?: ProductListViewType;
}

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
});

const mapDispatchToProps = {
    loadVariantChildren,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps & HasProductContext;

export interface ProductListProductInformationStyles {
    container?: GridContainerProps;
    brandStyles?: ProductBrandStyles;
    descriptionStyles?: ProductDescriptionStyles;
    brandGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    partNumbersGridItem?: GridItemProps;
    swatchGridItem?: GridItemProps;
    imageBadgeGridItem?: GridItemProps;
    textBadgeGridItem?: GridItemProps;
    partNumbersStyles?: ProductPartNumbersStyles;
    availabilityGridItem?: GridItemProps;
    availabilityStyles?: ProductAvailabilityStyles;
    attributesGridItem?: GridItemProps;
    attributes?: ProductAttributesStyles;
    searchScoreDataGridItem?: GridItemProps;
}

export const productInformationStyles: ProductListProductInformationStyles = {
    container: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            > *:not(:last-child) {
                margin-bottom: 14px;
                line-height: 0;
                &:first-child {
                    margin-bottom: 8px;
                }
            }
        `,
    },
    imageBadgeGridItem: {
        width: 12,
    },
    textBadgeGridItem: {
        width: 12,
    },
};

const styles = productInformationStyles;

const ProductListProductInformation = ({
    productContext,
    settingsCollection,
    showBrand,
    showTitle,
    showPartNumbers,
    showAvailability,
    showAttributes,
    showImageBadges,
    maxImageBadges,
    showTextBadges,
    maxTextBadges,
    showReorderPadProductAttributes,
    showPreviousPurchase,
    view,
    loadVariantChildren,
}: Props) => {
    const { product } = productContext;

    // ===================================================================
    // ALL HOOKS MUST BE DECLARED BEFORE ANY EARLY RETURNS (Rules of Hooks)
    // ===================================================================

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    const dispatch = useAppDispatch();

    // Read variant selection from custom Redux state
    const variantSelection = useAppSelector(state =>
        product?.isVariantParent
            ? ((state as any).xProductListVariants?.variantSelectionsByProductId?.[product.id] ?? {})
            : {},
    );

    // Read loaded variant children from products store
    const variantChildren = useAppSelector(state =>
        getVariantChildrenDataView(state, product?.isVariantParent ? product.id : undefined).value,
    );

    // On mount: pre-select the first in-stock variant. While the variant children (which carry the
    // stock flag) are still loading, temporarily fall back to the first available value per trait so
    // the card isn't blank; once the children arrive, upgrade the selection to the first in-stock
    // child. The manual-change guard prevents clobbering a user's own dropdown selection.
    const manuallyChangedRef = useRef(false);
    const inStockAppliedForProductRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (!product?.isVariantParent || !product.variantTraits) {
            return;
        }
        if (manuallyChangedRef.current) {
            return;
        }

        // Once the variant children are loaded, select the first in-stock child (falling back to the
        // first child when none report in stock). Apply this only once per product.
        if (variantChildren && variantChildren.length > 0 && inStockAppliedForProductRef.current !== product.id) {
            const firstInStockChild = variantChildren.find(isVariantChildStocked) ?? variantChildren[0];
            if (firstInStockChild?.childTraitValues && firstInStockChild.childTraitValues.length > 0) {
                firstInStockChild.childTraitValues.forEach(childTraitValue => {
                    dispatch({
                        type: "XKeller/UpdateProductListVariantSelection",
                        parentProductId: product.id,
                        variantTraitId: childTraitValue.styleTraitId,
                        traitValueId: childTraitValue.id,
                    });
                });
                inStockAppliedForProductRef.current = product.id;
                return;
            }
        }

        // Loading fallback: fill any unselected trait with its first available value.
        product.variantTraits.forEach(trait => {
            if (!variantSelection[trait.id] && trait.traitValues && trait.traitValues.length > 0) {
                const firstAvailable = trait.traitValues.find(tv => !tv.isDisabled) ?? trait.traitValues[0];
                dispatch({
                    type: "XKeller/UpdateProductListVariantSelection",
                    parentProductId: product.id,
                    variantTraitId: trait.id,
                    traitValueId: firstAvailable.id,
                });
            }
        });
        // loadVariantChildren is handled by ProductListProductContext override
    }, [product?.id, product?.isVariantParent, variantChildren, variantSelection]);

    // Dispatch variant trait selection change
    const handleUpdateVariantSelection = useCallback(
        (traitValueId: string, variantTraitId: string) => {
            if (!product) return;
            manuallyChangedRef.current = true;
            dispatch({
                type: "XKeller/UpdateProductListVariantSelection",
                parentProductId: product.id,
                variantTraitId,
                traitValueId,
            });
        },
        [dispatch, product?.id],
    );

    // Find the variant matching all selected trait values
    const selectedVariant: ProductModel | undefined = useMemo(() => {
        if (!variantChildren || !variantSelection || Object.keys(variantSelection).length === 0) {
            return undefined;
        }
        return variantChildren.find(child =>
            child.childTraitValues?.every(ctv => variantSelection[ctv.styleTraitId] === ctv.id),
        );
    }, [variantChildren, variantSelection]);

    // The shared ProductContextAvailability component hides itself whenever product.isVariantParent is
    // true (the parent has no inventory of its own). Once a variant is fully resolved, override the
    // context with the variant's real ProductModel so availability actually renders (mirrors how the
    // PDP swaps in the selected variant's product for pricing/availability).
    const availabilityContext: ProductContextModel | undefined = useMemo(() => {
        if (!selectedVariant) {
            return undefined;
        }
        return { ...productContext, product: selectedVariant };
    }, [productContext, selectedVariant]);

    // ===================================================================
    // EARLY RETURN GUARD (after all hooks)
    // ===================================================================
    if (!product) {
        return null;
    }

    const sortedVariantTraits = product.isVariantParent && product.variantTraits && product.variantTraits.length > 0
        ? product.variantTraits.slice().sort((a, b) => a.sortOrder - b.sortOrder)
        : [];

    // XNG-Changes: Update structure
    return (
        <StyledWrapper {...styles.container}>
            {product.brand && showBrand && <ProductBrand brand={product.brand} extendedStyles={styles.brandStyles} />}
            {showTitle && (
                <ProductDescription product={productContext} extendedStyles={styles.descriptionStyles} view={view} />
            )}
            {showPartNumbers && (
                // XNG-Changes: KSC-453: Hide productNumber if a product has customerProductNumber
                // XNG-Changes: Show Item # for variant parents; when a variant is selected show its numbers
                <ProductPartNumbers
                    productNumber={selectedVariant?.productNumber || product.productNumber}
                    customerProductNumber={product.customerProductNumber}
                    showProductNumber={!product.customerProductNumber}
                    manufacturerItem={selectedVariant?.manufacturerItem || product.manufacturerItem}
                    modelNumber={product.properties?.modelnumber || product.properties?.ModelNumber}
                    extendedStyles={styles.partNumbersStyles}
                />
            )}
            {sortedVariantTraits.map(variantTrait => (
                <ProductDetailsVariantDropdown
                    key={variantTrait.id}
                    variantSelection={variantSelection}
                    variantTrait={variantTrait}
                    updateVariantSelection={handleUpdateVariantSelection}
                />
            ))}
            {showTextBadges && textBadges && (
                <>
                    <ProductTextBadges badgeList={textBadges} maxBadges={maxTextBadges ?? 4} />{" "}
                </>
            )}
            {showImageBadges && imageBadges && (
                <ProductImageBadges badgeList={imageBadges} maxBadges={maxImageBadges ?? 4} />
            )}
            {showAvailability &&
                (availabilityContext ? (
                    <ProductContext.Provider value={availabilityContext}>
                        <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                    </ProductContext.Provider>
                ) : (
                    <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                ))}
            {showPreviousPurchase && <ProductPreviousPurchase product={productContext.product} />}
            {showAttributes && <ProductAttributes maximumNumberAttributeTypes={3} extendedStyles={styles.attributes} />}
            {/** XNG-Changes: add this component for ReorderPad page */}
            {showReorderPadProductAttributes && <ReorderPadProductAttributes extendedStyles={styles.attributes} />}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductListProductInformation));
